import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import logoLightAsset from "@/assets/brand/bootpreneurs-logo-2026-light.png.asset.json";
import logoDarkAsset from "@/assets/brand/bootpreneurs-logo-2026-dark.png.asset.json";

const logoLight = logoLightAsset.url;
const logoDark = logoDarkAsset.url;

interface LogoProps {
  variant?: "wordmark" | "mark";
  size?: number;
  className?: string;
  asLink?: boolean;
  ariaLabel?: string;
}

/**
 * Single source of truth for the Bootpreneurs brand mark.
 *
 * Renders the official wordmark PNG (BO + lightbulb + OT + PREN + EURS
 * with arrow swoosh). A dark-mode variant (white "BO/OT" letters) is
 * stacked alongside the light variant and toggled purely with Tailwind
 * `dark:` visibility classes — no JS theme read, no hydration flicker.
 *
 * `variant="mark"` renders only the lightbulb glyph by left-anchoring an
 * `object-cover` square crop, so we don't need a third asset.
 */
export function Logo({
  variant = "wordmark",
  size,
  className = "w-36 h-auto",
  asLink = true,
  ariaLabel = "Bootpreneurs — home",
}: LogoProps) {
  const isMark = variant === "mark";

  // Source PNGs are roughly 4:1 wordmark crops.
  const heightPx = typeof size === "number" ? size : undefined;
  const sizeStyle = heightPx ? { height: heightPx } : undefined;

  // Shared <img> classes. For the mark variant, left-anchor a square crop so
  // only the lightbulb glyph shows. For the wordmark, follow whatever axis
  // the wrapper className anchors on: height-anchored (`h-…`) → fill height,
  // width-anchored (default `w-…`) → fill width.
  const heightAnchored = /(^|\s)h-(?!auto)/.test(className);
  const baseImg = isMark
    ? "h-full w-auto aspect-square object-cover object-left"
    : heightPx || heightAnchored
      ? "h-full w-auto"
      : "w-full h-auto";

  const wrapperClass = heightPx ? "inline-flex items-center" : className;

  const content = (
    <span
      className={`inline-flex items-center ${heightPx ? "" : "h-full"}`}
      style={sizeStyle}
      aria-label={ariaLabel}
      role="img"
    >
      <img
        src={logoLight}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        draggable={false}
        className={`${baseImg} block dark:hidden`}
      />
      <img
        src={logoDark}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        draggable={false}
        className={`${baseImg} hidden dark:block`}
      />
    </span>
  );

  if (!asLink) {
    return (
      <span className={wrapperClass} style={sizeStyle}>
        {content}
      </span>
    );
  }
  return (
    <Link
      to="/"
      aria-label={ariaLabel}
      className={`inline-flex items-center ${wrapperClass}`}
      style={sizeStyle}
    >
      {content}
    </Link>
  );
}
